import { Alert, Form, Modal, Select } from 'antd';
import { IDocumentTemplate } from 'interfaces/documentation/document-template';
import { useEffect } from 'react';
import { useModalReturnType, useUpdate } from '@refinedev/core';

interface RequiredModalProps {
  modal: useModalReturnType;
  template?: IDocumentTemplate;
  resource: string;
}

export const RequiredModal = ({
  modal,
  template,
  resource
}: RequiredModalProps) => {
  const { mutateAsync: updateRecord } = useUpdate();
  const [form] = Form.useForm();

  useEffect(() => {
    if (template) {
      form.setFieldsValue({ required: template.required });
    }
  }, [template, form]);

  if (!template) {
    return null;
  }

  // Sem isto, mexer no Select e cancelar deixa a escolha não salva no formulário: reabrir a mesma
  // linha mostraria o valor descartado em vez do atual, já que o useEffect só dispara quando o
  // template muda. Restaura o valor commitado em vez de limpar, que deixaria o campo vazio.
  const handleCancel = () => {
    form.setFieldsValue({ required: template.required });
    modal.close();
  };

  const submit = () => {
    updateRecord({
      resource,
      id: template.id,
      values: { required: form.getFieldValue('required') },
      errorNotification: {
        type: 'error',
        message: 'Ocorreu algum erro interno no serviço',
        description: 'Erro ao alterar a obrigatoriedade do documento!'
      }
    })
      .then(() => {
        form.resetFields();
        modal.close();
      })
      .catch(() => null);
  };

  const handleUpdate = () => {
    // Dispensar um documento de identificação vale confirmação: o efeito é para o tenant inteiro
    // e não é simétrico — religar depois não reavalia quem já foi aprovado no intervalo.
    const droppingIdentification =
      template.category === 'identification' &&
      template.required &&
      form.getFieldValue('required') === false;

    if (!droppingIdentification) {
      submit();
      return;
    }

    Modal.confirm({
      title: `Tornar "${template.name}" opcional?`,
      okText: 'Sim, tornar opcional',
      okType: 'danger',
      cancelText: 'Cancelar',
      width: 520,
      content: (
        <>
          <p>
            Este é um documento de <strong>identificação</strong>. A partir de
            agora nenhum aluno precisará enviá-lo para ter a documentação
            aprovada.
          </p>
          <p>
            Reverter depois não desfaz: as documentações aprovadas enquanto ele
            estiver opcional continuam aprovadas, e teriam que ser reprocessadas
            uma a uma.
          </p>
        </>
      ),
      onOk: submit
    });
  };

  return (
    <Modal
      title={`Editar obrigatoriedade — ${template.name}`}
      width={400}
      visible={modal.visible}
      onCancel={handleCancel}
      onOk={handleUpdate}
    >
      <Form form={form} layout="vertical">
        {template.required_courses_count > 0 && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message={`${template.required_courses_count} ${
              template.required_courses_count === 1
                ? 'curso exige'
                : 'cursos exigem'
            } este documento por conta própria`}
            description="Marcar como opcional aqui não afeta esses cursos: a obrigatoriedade por curso é uma configuração separada, editada em Associação template x curso."
          />
        )}

        <Form.Item name="required" label="Obrigatoriedade" required>
          <Select placeholder="Selecione a obrigatoriedade">
            <Select.Option value={true}>Obrigatório</Select.Option>
            <Select.Option value={false}>Opcional</Select.Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RequiredModal;
